import { ChannelData } from "@dashboard/channels/utils";
import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid, { GetCellContentOpts } from "@dashboard/components/Datagrid/Datagrid";
import { DatagridChangeOpts } from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import {
  AttributeInputTypeEnum,
  ProductDetailsVariantFragment,
  ProductFragment,
  RefreshLimitsQuery,
} from "@dashboard/graphql";
import useStateFromProps from "@dashboard/hooks/useStateFromProps";
import EditIcon from "@dashboard/icons/Edit";
import { buttonMessages } from "@dashboard/intl";
import { ProductVariantListError } from "@dashboard/products/views/ProductUpdate/handlers/errors";
import { Item } from "@glideapps/glide-data-grid";
import { Button } from "@saleor/macaw-ui";
import { Option } from "@saleor/macaw-ui-next";
import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ProductVariantsHeader } from "./components/ProductVariantsHeader";
import {
  useAttributesAdapter,
  useChannelAdapter,
  useWarehouseAdapter,
  variantsStaticColumnsAdapter,
} from "./datagrid";
import messages from "./messages";
import { getData, getError } from "./utils";
import { IntlShape } from "react-intl";

interface ProductVariantsProps {
  channels: ChannelData[];
  errors: ProductVariantListError[];
  limits: RefreshLimitsQuery["shop"]["limits"];
  variantAttributes: ProductFragment["productType"]["variantAttributes"];
  variants: ProductDetailsVariantFragment[];
  productName: string;
  productId: string;
  onAttributeValuesSearch: (id: string, query: string) => Promise<Option[]>;
  onChange: (data: DatagridChangeOpts) => void;
  onRowClick: (id: string) => void;
  initialFullscreen?: boolean; // Start in fullscreen bulk edit mode
  onBulkDelete?: (variantIds: string[]) => Promise<void>; // Add this prop
}

export const ProductVariants = ({
  channels,
  errors,
  variants,
  variantAttributes,
  productName,
  productId,
  onAttributeValuesSearch,
  onChange,
  onRowClick,
  initialFullscreen = false,
  onBulkDelete, // Add this
}: ProductVariantsProps) => {
  const intl = useIntl();

  // https://github.com/saleor/saleor-dashboard/issues/4165
  // const { data: warehousesData } = useWarehouseListQuery({
  //   variables: {
  //     first: 50,
  //   },
  // });
  // const warehouses = mapEdgesToItems(warehousesData?.warehouses);

  // Normally this should be in LS handled by useListSettings hook
  // https://github.com/saleor/saleor-dashboard/issues/4164

  const initialSettings = useMemo(() => {
    console.log("=== ProductVariants initialSettings ===");
    console.log("channels:", channels?.length);
    console.log("variantAttributes:", variantAttributes?.length);
    console.log("variantAttributes:", variantAttributes);

    if (!channels || !variantAttributes) {
      console.log("Missing channels or variantAttributes, returning undefined");
      return undefined;
    }

    const filteredAttributes = variantAttributes?.filter(
      attribute =>
        attribute.inputType === AttributeInputTypeEnum.DROPDOWN ||
        attribute.inputType === AttributeInputTypeEnum.PLAIN_TEXT,
    );

    console.log("Filtered attributes (DROPDOWN or PLAIN_TEXT):", filteredAttributes?.length);
    console.log("Filtered attributes:", filteredAttributes);

    const settings = [
      "name",
      // "sku",
      "costPrice", // ensure it’s part of the defaults
      ...channels.flatMap(channel => [`channel:${channel.id}`]),
      ...(filteredAttributes?.map(attribute => `attribute:${attribute.id}`) ?? []),
    ];

    console.log("Final initialSettings:", settings);
    return settings;
  }, [channels, variantAttributes]);
  // For global variants (no productId), use React.useState to avoid loading saved preferences
  // For regular variants, use useStateFromProps to load saved column preferences
  const [columnSettings, setColumnSettings] = !productId
    ? React.useState<string[] | undefined>(initialSettings)
    : useStateFromProps<string[] | undefined>(initialSettings);

  // after columnSettings is defined
  React.useEffect(() => {
    if (columnSettings && !columnSettings.includes("costPrice")) {
      setColumnSettings([...columnSettings, "costPrice"]);
    }
  }, [columnSettings, setColumnSettings]);

  console.log("productId:", productId);
  console.log("Using useState (global)?", !productId);
  console.log("columnSettings after hook:", columnSettings);
  console.log("columnSettings length:", columnSettings?.length);

  React.useEffect(() => {
    if (columnSettings) {
      handlers.onResetDynamicToInitial();
    }
  }, [columnSettings]);

  const handleColumnChange = React.useCallback(
    (picked: string[]) => {
      setColumnSettings(picked);
    },
    [setColumnSettings],
  );

  // const limitReached = isLimitReached(limits, "productVariants");

  const channelCategory = useChannelAdapter({
    intl,
    listings: channels,
    selectedColumns: columnSettings,
    defaultChannelId: channels?.[0]?.id, // treat first channel as default (or pass real defaultChannelId)
  });
  const attributeCategory = useAttributesAdapter({
    intl,
    selectedColumns: columnSettings,
    attributes: variantAttributes,
  });
  const memoizedStaticColumns = useMemo(() => variantsStaticColumnsAdapter(intl), [intl]);
  const {
    handlers,
    columnCategories,
    visibleColumns,
    staticColumns,
    dynamicColumns,
    selectedColumns,
    recentlyAddedColumn,
  } = useColumns({
    gridName: "variants",
    staticColumns: memoizedStaticColumns,
    columnCategories: [channelCategory, attributeCategory], // only channel + attributes
    selectedColumns: columnSettings ?? [],
    onSave: handleColumnChange,
  });

  console.log("=== AFTER useColumns ===");
  console.log("visibleColumns length:", visibleColumns?.length);
  console.log("selectedColumns length:", selectedColumns?.length);
  console.log("visibleColumns:", visibleColumns);

  const getCellContent = React.useCallback(
    ([column, row]: Item, opts: GetCellContentOpts) =>
      getData({
        availableColumns: visibleColumns,
        column,
        row,
        channels,
        variants,
        searchAttributeValues: onAttributeValuesSearch,
        ...opts,
      }),
    [channels, visibleColumns, onAttributeValuesSearch, variants],
  );
  const getCellError = React.useCallback(
    ([column, row]: Item, opts: GetCellContentOpts) =>
      getError(errors, {
        availableColumns: visibleColumns,
        column,
        row,
        channels,
        variants,
        searchAttributeValues: onAttributeValuesSearch,
        ...opts,
      }),
    [errors, visibleColumns, channels, variants, onAttributeValuesSearch],
  );

  const handleBulkDelete = React.useCallback(
    async (indexes: number[], { removeRows }: { removeRows: (indexes: number[]) => void }) => {
      if (!onBulkDelete) {
        // Fallback to local removal if no handler provided
        removeRows(indexes);
        return;
      }

      // Get variant IDs from selected indexes
      const variantIds = indexes
        .map(index => variants[index]?.id)
        .filter((id): id is string => !!id);

      if (variantIds.length === 0) {
        return;
      }

      try {
        await onBulkDelete(variantIds);
        // After successful deletion, remove rows locally
        removeRows(indexes);
      } catch (error) {
        // Error handling is done in the parent component
        console.error("Bulk delete error:", error);
      }
    },
    [variants, onBulkDelete],
  );

  return (
    <Datagrid
      fillHandle={true}
      renderHeader={props => (
        <ProductVariantsHeader {...props} productId={productId} productName={productName} />
      )}
      availableColumns={visibleColumns}
      emptyText={intl.formatMessage(messages.empty)}
      getCellContent={getCellContent}
      getCellError={getCellError}
      menuItems={index => [
        {
          label: "Edit Variant",
          onSelect: () => onRowClick(variants[index].id),
          Icon: <EditIcon />,
        },
      ]}
      rows={variants?.length ?? 0}
      selectionActions={(indexes, actions) => (
        <Button
          data-test-id="bulk-delete-button"
          variant="tertiary"
          onClick={() => handleBulkDelete(indexes, actions)}
        >
          <FormattedMessage {...buttonMessages.delete} />
        </Button>
      )}
      onColumnResize={handlers.onResize}
      onColumnMoved={handlers.onMove}
      renderColumnPicker={() => (
        <ColumnPicker
          staticColumns={staticColumns}
          dynamicColumns={dynamicColumns}
          selectedColumns={selectedColumns}
          columnCategories={columnCategories}
          onToggle={handlers.onToggle}
          side="left"
        />
      )}
      onChange={onChange}
      recentlyAddedColumn={recentlyAddedColumn}
      initialFullscreen={initialFullscreen}
    />
  );
};
ProductVariants.displayName = "ProductVariants";
export default ProductVariants;

export const useChannelAdapter = ({
  intl,
  selectedColumns,
  listings,
  defaultChannelId,
}: {
  intl: IntlShape;
  selectedColumns: string[] | undefined;
  listings: ChannelData[] | undefined;
  defaultChannelId?: string;
}): ColumnCategory => {
  const parseChannelColumns = (data: ChannelData[] | undefined) =>
    data?.flatMap(channel => [
      {
        id: `availableInChannel:${channel.id}`,
        group: "", // keep group empty for availability (we don't use availability in ProductVariants)
        metaGroup: "",
        title: channel.name ?? "",
        width: 80,
      },
      {
        id: `channel:${channel.id}`,
        group: "", // clear the group title so group header is not shown
        metaGroup: "",
        // If this channel is the default channel, show "Price" instead of the channel name
        title:
          channel.id === defaultChannelId
            ? intl.formatMessage({ id: "productVariants.price", defaultMessage: "Price" })
            : (channel.name ?? ""),
        width: 150,
      },
      {
        id: `channel:${channel.id}`,
        group: "",
        metaGroup: "",
        title:
          channel.id === defaultChannelId
            ? intl.formatMessage({ id: "variants.costPrice", defaultMessage: "Cost Price" })
            : (channel.name ?? ""),
        width: 150,
      },
    ]);

  const filtered = listings?.filter(l => l !== undefined);

  return {
    // Clear the category title to "comment out" Default Channel header
    name: "",
    prefix: "channel",
    availableNodes: parseChannelColumns(filtered),
    selectedNodes: parseChannelColumns(
      selectedColumns
        ? filtered?.filter(ch => selectedColumns.includes(`channel:${ch.id}`))
        : filtered,
    ),
    // keep pagination/search no-ops as before
    onSearch: () => {},
    initialSearch: "",
    hasNextPage: false,
    hasPreviousPage: false,
    onNextPage: () => {},
    onPreviousPage: () => {},
  };
};

/* Commented out warehouse adapter and availability adapter bodies so those columns are not available.
   If you need to re-enable them later, uncomment and adjust as needed. */

// export const useChannelAvailabilityAdapter = ({
//   intl,
//   selectedColumns,
//   listings,
// }: {
//   intl: IntlShape;
//   selectedColumns: string[] | undefined;
//   listings: ChannelData[] | undefined;
// }): ColumnCategory => {
//   // Previously created "Available" column per channel.
//   // Now commented out to remove the "Available" column from the variants datagrid.
//   return {
//     name: "",
//     prefix: "availableInChannel",
//     availableNodes: [],
//     selectedNodes: [],
//     onSearch: () => {},
//     initialSearch: "",
//     hasNextPage: false,
//     hasPreviousPage: false,
//     onNextPage: () => {},
//     onPreviousPage: () => {},
//   };
// };

// export const useWarehouseAdapter = ({
//   intl,
//   selectedColumns,
//   warehouses,
// }: {
//   intl: IntlShape;
//   selectedColumns: string[] | undefined;
//   warehouses: WarehouseFragment[] | undefined;
// }) => {
//   // Previously produced warehouse columns. Commented out to hide warehouse columns.
//   return {
//     name: "",
//     prefix: "warehouse",
//     availableNodes: [],
//     selectedNodes: [],
//     onSearch: () => {},
//     initialSearch: "",
//     hasNextPage: false,
//     hasPreviousPage: false,
//     onNextPage: () => {},
//     onPreviousPage: () => {},
//   };
// };
